// 首頁主視覺輪播：本週發售（本週沒有時為最受期待）。
// - 每 8 秒換下一款；計時由進度條的 CSS 動畫驅動（動畫結束 → 下一張），所以「暫停」只要暫停動畫，
//   進度條與實際計時永遠一致。
// - 滑鼠移入、鍵盤焦點在輪播內、分頁不在前景、使用者按暫停、系統開啟「減少動態」時不自動換頁。
// - 手機可以左右滑動（捲動吸附），滑動後同步目前的索引。
// - 點小卡時是程式捲動：捲動途中經過的其他張不能回寫索引，否則會和目標互相拉扯（GDO-33）。
import { fromMask, PLATFORMS, type PlatformKey } from '@dayone/core/dictionaries';
import { memo, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CatalogItem } from '../../filters/catalog.ts';
import { daysUntil } from '../../filters/timeline.ts';
import { t } from '../../i18n/zh-TW.ts';
import { countdown, formatRelease, imageUrl } from '../../lib/format.ts';
import { ChevronRight, ICON, Pause, Play } from '../../ui/icons/index.ts';
import { PlatformList } from '../../ui/PlatformList.tsx';

export const HERO_INTERVAL_MS = 8000;

function useDocumentVisible(): boolean {
  return useSyncExternalStore(
    (cb) => {
      document.addEventListener('visibilitychange', cb);
      return () => document.removeEventListener('visibilitychange', cb);
    },
    () => document.visibilityState === 'visible',
    () => true,
  );
}

function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );
}

interface Props {
  mode: 'week' | 'hype';
  items: CatalogItem[];
  /** 使用者時區的今天 */
  today: number;
  currentYear: number;
  onOpen: (id: string) => void;
}

export const HeroCarousel = memo(function HeroCarousel({ mode, items, today, currentYear, onOpen }: Props) {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const visible = useDocumentVisible();
  const reduced = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  /** 程式捲動的目標索引；到達或捲動結束前，捲動事件不回寫索引 */
  const programmatic = useRef<number | null>(null);
  const count = items.length;
  const current = Math.min(index, count - 1);
  const autoplay = count > 1 && !reduced;
  const paused = !autoplay || userPaused || hovered || focused || !visible;

  // 清單改變（例如切換平台）時回到第一張
  const idsKey = items.map((i) => i.row.id).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在輪播內容改變時重設
  useEffect(() => setIndex(0), [idsKey]);

  // 切換時把軌道捲到對應的一張（手機滑動時由捲動事件反向同步）
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const left = current * track.clientWidth;
    if (Math.abs(track.scrollLeft - left) < 2) return;
    programmatic.current = current;
    track.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }, [current, reduced]);

  /** 使用者滑動：停在哪一張就同步到哪一張 */
  const sync = useCallback(() => {
    const track = trackRef.current;
    if (!track?.clientWidth) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    setIndex((prev) => (prev === i ? prev : i));
  }, []);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track?.clientWidth) return;
    const target = programmatic.current;
    if (target === null) return sync();
    // 程式捲動中：只有到達目標那一張才結束，途中經過的張數不算（例如從第 3 張往左捲到第 1 張會經過第 2 張）
    if (Math.abs(track.scrollLeft - target * track.clientWidth) < 2) programmatic.current = null;
  }, [sync]);

  // 捲動結束（包含使用者中途用手指打斷程式捲動）：以停下來的位置為準
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !('onscrollend' in track)) return;
    const onEnd = () => {
      programmatic.current = null;
      sync();
    };
    track.addEventListener('scrollend', onEnd);
    return () => track.removeEventListener('scrollend', onEnd);
  }, [sync]);

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  if (count === 0) return null;
  const label = mode === 'week' ? t.hero.week : t.hero.hype;

  return (
    <section
      className="hero"
      aria-roledescription="carousel"
      aria-label={label}
      data-paused={paused ? '' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <div ref={trackRef} className="hero-track" onScroll={onScroll}>
        {items.map((item, i) => (
          <Slide
            key={item.row.id}
            item={item}
            active={i === current}
            index={i}
            count={count}
            eyebrow={label}
            note={mode === 'hype' ? t.hero.hypeNote : undefined}
            today={today}
            currentYear={currentYear}
            onOpen={onOpen}
          />
        ))}
      </div>

      {count > 1 && (
        <div className="hero-controls">
          <div className="hero-dots">
            {items.map((item, i) => (
              <button
                key={item.row.id}
                type="button"
                className={i === current ? 'hero-dot on' : 'hero-dot'}
                aria-label={t.hero.goTo(item.row.n)}
                aria-current={i === current ? 'true' : undefined}
                onClick={() => setIndex(i)}
              >
                {item.row.c && (
                  <img className="hero-thumb" src={imageUrl(item.row.c, 'cover_small') ?? ''} alt="" loading="lazy" />
                )}
                {i === current && autoplay && (
                  <span
                    // key 讓每一張重新開始計時
                    key={`${item.row.id}-${current}`}
                    className="hero-progress"
                    style={{ animationDuration: `${HERO_INTERVAL_MS}ms` }}
                    onAnimationEnd={next}
                  />
                )}
              </button>
            ))}
          </div>
          {autoplay && (
            <button
              type="button"
              className="hero-pause"
              aria-label={userPaused ? t.hero.play : t.hero.pause}
              aria-pressed={userPaused}
              onClick={() => setUserPaused((p) => !p)}
            >
              {userPaused ? <Play {...ICON} /> : <Pause {...ICON} />}
            </button>
          )}
        </div>
      )}
    </section>
  );
});

interface SlideProps {
  item: CatalogItem;
  active: boolean;
  index: number;
  count: number;
  eyebrow: string;
  note?: string;
  today: number;
  currentYear: number;
  onOpen: (id: string) => void;
}

function Slide({ item, active, index, count, eyebrow, note, today, currentYear, onOpen }: SlideProps) {
  const { row } = item;
  const days = daysUntil(item, today);
  const cd = days !== null && days >= 0 ? countdown(days) : null;
  const platforms = fromMask(PLATFORMS, row.p) as PlatformKey[];
  const src = (size: Parameters<typeof imageUrl>[1]) => imageUrl(row.a, size) ?? undefined;
  return (
    <article
      className={active ? 'hero-slide active' : 'hero-slide'}
      aria-roledescription="slide"
      aria-label={t.hero.slide(index + 1, count)}
      inert={!active}
    >
      <img
        className="hero-bg"
        src={src('screenshot_big')}
        srcSet={`${src('screenshot_big')} 889w, ${src('screenshot_huge')} 1280w, ${src('1080p')} 1920w`}
        sizes="(min-width: 1024px) calc(100vw - 280px), 100vw"
        alt=""
        loading={index === 0 ? 'eager' : 'lazy'}
        fetchPriority={index === 0 ? 'high' : 'auto'}
        decoding="async"
      />
      <div className="hero-body">
        <p className="hero-eyebrow">
          {eyebrow}
          {note && <span className="hero-note">{note}</span>}
        </p>
        <h2 className="hero-title">{row.n}</h2>
        {row.z && <p className="hero-zh">{row.z}</p>}
        <div className="hero-meta">
          {cd ? (
            <p className={days === 0 ? 'hero-count today' : 'hero-count'}>
              {days !== 0 && <span className="hero-count-value num">{cd.value}</span>}
              <span className="hero-count-unit">
                {cd.unit}
                <span className="hero-count-date num">{formatRelease(item.date, currentYear)}</span>
              </span>
            </p>
          ) : (
            <p className="hero-count">
              <span className="hero-count-value num">{formatRelease(item.date, currentYear)}</span>
            </p>
          )}
          <PlatformList platforms={platforms} />
        </div>
        <button type="button" className="btn btn-primary hero-cta" onClick={() => onOpen(row.id)}>
          {t.hero.details}
          <ChevronRight {...ICON} />
        </button>
      </div>
    </article>
  );
}
